import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { onHoldsReleased, safeToReload } from './platform/update';

/**
 * The service worker update flow (ARCHITECTURE §14.1, §12).
 *
 * An installed PWA otherwise keeps serving the precached build it started with,
 * and a deploy silently never reaches the phone. This component is what ends
 * that — it now **applies** the update rather than offering it. Why that
 * changed, and what still holds it off, is argued in `platform/update.ts`.
 *
 * Two halves, and the first one was missing entirely:
 *
 * 1. **Noticing.** `useRegisterSW` checks for a new worker once, when it
 *    registers — which is when the page loads. An installed PWA is resumed far
 *    more often than it is loaded, so a phone that is never fully closed never
 *    asks again and a deploy from Tuesday is still not on it on Friday. The
 *    checks below run on the same triggers the sync loop uses (back on screen,
 *    back online) plus a slow poll, for the same reason: a screen that decided
 *    "is this current?" on different signals from the data underneath it would
 *    be a second answer to the same question.
 *
 * 2. **Applying.** As soon as one is ready, at the first moment `safeToReload()`
 *    says costs nothing. Where it says otherwise the banner appears and the
 *    attempt is repeated — on every resume, on every released hold, and on a
 *    slow retry — so a deferral lasts as long as the typing does and no longer.
 */

/** How often an open app asks whether a newer build has been deployed. */
const CHECK_MS = 30 * 60 * 1000;

/** How often a held-off update reconsiders, when nothing else has prompted it to. */
const RETRY_MS = 60 * 1000;

export function UpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const [held, setHeld] = useState(false);
  // Reloading is the last thing this page does, but `updateServiceWorker`
  // resolves asynchronously — without a latch the retry timer can fire again in
  // the gap and ask a second time.
  const applying = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW: (_swUrl, r) => {
      if (r) setRegistration(r);
    },
    onRegisterError: (error) => console.error('service worker registration failed', error),
  });

  // `useRegisterSW` hands back a fresh closure each render; holding it in a ref
  // keeps the effect below from re-subscribing on every one of them.
  const applyRef = useRef(updateServiceWorker);
  applyRef.current = updateServiceWorker;

  useEffect(() => {
    if (!registration) return;

    // A failed check is not worth a word: it means the network is down, which
    // this app has a whole offline story for.
    const check = () => void registration.update().catch(() => {});
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(onVisible, CHECK_MS);

    return () => {
      window.removeEventListener('online', check);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [registration]);

  useEffect(() => {
    if (!needRefresh) return;

    const attempt = () => {
      if (applying.current) return;
      if (!safeToReload()) {
        setHeld(true);
        return;
      }
      applying.current = true;
      // `true` activates the waiting worker and reloads once it has taken over,
      // so the reloaded page is served by the new build rather than racing it.
      void applyRef.current(true);
    };

    // Both directions of `visibilitychange` are worth a try: going away is the
    // safest moment there is, and coming back is the moment before anyone has
    // touched anything.
    document.addEventListener('visibilitychange', attempt);
    const release = onHoldsReleased(attempt);
    const timer = window.setInterval(attempt, RETRY_MS);
    attempt();

    return () => {
      document.removeEventListener('visibilitychange', attempt);
      release();
      window.clearInterval(timer);
    };
  }, [needRefresh]);

  // Nothing is said about an update that applies itself in the same breath —
  // the banner is only for the case where the app is knowingly running a build
  // it knows is old, which is worth saying out loud.
  if (!needRefresh || !held) return null;

  return (
    <div className="banner">
      Nieuwe versie klaar — die laadt zodra je hier klaar bent.
      <button
        className="btn btn-quiet"
        style={{ marginLeft: 'auto', minHeight: 32 }}
        onClick={() => {
          applying.current = true;
          void applyRef.current(true);
        }}
      >
        Nu herladen
      </button>
    </div>
  );
}
